import { useState, useEffect } from 'react'
import styles from './ConnectorConfigModal.module.css'
import Button from './Button'
import Icon from './Icon'
import ConfirmDialog from './ConfirmDialog'
import TransformersTab from './TransformersTab'
import { fetchConnectorProps, updateConnectorProp } from '../api/connectors'

const TABS = [
  { key: 'actions', label: 'Actions' },
  { key: 'transformers', label: 'Transformers' },
]

// Stable per-row identity, since rows can be added/removed and positional index isn't safe to key off of.
let nextRowId = 0
function makeRowId() {
  return `row-${Date.now()}-${nextRowId++}`
}

/**
 * ConnectorConfigModal — shown when "Configure" is clicked or when an error
 * status pill is clicked on an ActiveConnectorCard.
 *
 * Props:
 *   isOpen       {boolean}         — controls visibility
 *   onClose      {function}        — called when the modal is dismissed
 *   connectorId  {string|number}   — used to fetch /connector/props/{connector_id}
 *   name         {string}          — connector name shown in the header
 *   status       {string}          — "active" | "error" — drives header accent colour
 */
export default function ConnectorConfigModal({ isOpen, onClose, connectorId, name, status, initialAction }) {
  const [tab, setTab] = useState('actions')
  const [propsLoading, setPropsLoading] = useState(false)
  const [propsError, setPropsError]     = useState(null)
  const [rows, setRows]                 = useState([])
  const [userAction, setUserAction]     = useState(null) // action picked in the filter dropdown, or null
  const [rowStatus, setRowStatus]       = useState({}) // rowId -> 'saving' | 'saved' | 'deleting' | 'error'
  const [newRowIds, setNewRowIds]       = useState(new Set()) // rowId of unsaved, user-added rows
  const [pendingDelete, setPendingDelete] = useState(null) // row awaiting delete confirmation, or null

  useEffect(() => {
    if (!isOpen || !connectorId) return
    setTab('actions')
    setRows([])
    setNewRowIds(new Set())
    setPropsError(null)
    setPropsLoading(true)
    fetchConnectorProps(connectorId)
      .then(data => setRows((data.properties ?? []).map(p => ({ ...p, _id: makeRowId() }))))
      .catch(e => setPropsError(e.message))
      .finally(() => setPropsLoading(false))
  }, [isOpen, connectorId])

  // Drop any manual filter choice whenever the modal opens or the pre-selected action changes.
  useEffect(() => {
    setUserAction(null)
  }, [isOpen, initialAction])

  // The filter is derived: an explicit dropdown choice wins, otherwise fall back to the
  // pre-selected action. `initialAction` is the pill label (e.g. "Closed Tickets"), matched
  // against the row's `display_name`, then resolved to that row's `action_name` value.
  const initialMatch = initialAction
    ? (rows.find(r => r.display_name === initialAction)?.action_name ?? initialAction)
    : '__all__'
  const selectedAction = userAction ?? initialMatch

  const isError = status?.toLowerCase() === 'error'

  // Unique action names for the dropdown, preserving insertion order
  const actionOptions = [...new Set(rows.map(r => r.action_name))]

  const filteredRows = rows.filter(r =>
    selectedAction === '__all__' || r.action_name === selectedAction || newRowIds.has(r._id)
  )

  function updateCell(id, field, value) {
    setRows(prev => prev.map(row => row._id === id ? { ...row, [field]: value } : row))
  }

  function handleAddProperty() {
    const id = makeRowId()
    setRows(prev => [...prev, {
      _id: id,
      action_name: selectedAction !== '__all__' ? selectedAction : '',
      prop_key: '',
      value: '',
    }])
    setNewRowIds(prev => new Set(prev).add(id))
  }

  async function handleUpdateRow(row) {
    const id = row._id
    setRowStatus(prev => ({ ...prev, [id]: 'saving' }))
    try {
      await updateConnectorProp(connectorId, {
        action_name: row.action_name,
        prop_key: row.prop_key,
        prop_value: row.value,
      })
      setRowStatus(prev => ({ ...prev, [id]: 'saved' }))
      setNewRowIds(prev => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      setTimeout(() => {
        setRowStatus(prev => {
          const { [id]: _, ...rest } = prev
          return rest
        })
      }, 1500)
    } catch {
      setRowStatus(prev => ({ ...prev, [id]: 'error' }))
    }
  }

  function handleDeleteRow(row) {
    setPendingDelete(row)
  }

  async function performDelete(row) {
    const id = row._id
    setPendingDelete(null)
    setRowStatus(prev => ({ ...prev, [id]: 'deleting' }))
    try {
      await updateConnectorProp(connectorId, {
        action_name: row.action_name,
        prop_key: row.prop_key,
        prop_value: row.value,
        delete: true,
      })
      setRows(prev => prev.filter(r => r._id !== id))
      setNewRowIds(prev => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      setRowStatus(prev => {
        const { [id]: _, ...rest } = prev
        return rest
      })
    } catch {
      setRowStatus(prev => ({ ...prev, [id]: 'error' }))
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={`${styles.header} ${isError ? styles.headerError : styles.headerActive}`}>
          <div className={styles.headerLeft}>
            <span className={`${styles.statusDot} ${isError ? styles.dotError : styles.dotActive}`} />
            <h2 className={styles.title}>{name} — {isError ? 'Error Details' : 'Configure'}</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close modal">
            <Icon name="close" />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {isError && (
            <div className={styles.errorNotice}>
              <Icon name="info" className={styles.errorIcon} />
              <p className={styles.errorText}>
                There was a problem syncing <strong>{name}</strong>. Review the connection
                settings below and re-authenticate if needed.
              </p>
            </div>
          )}

          {/* ── Tabs ── */}
          <div className={styles.tabs} role="tablist">
            {TABS.map(t => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'transformers' && (
            <TransformersTab connectorId={connectorId} initialAction={initialAction} />
          )}

          {tab === 'actions' && propsLoading && (
            <p className={styles.placeholder}>Loading configuration…</p>
          )}

          {tab === 'actions' && propsError && !propsLoading && (
            <p className={styles.placeholder}>Failed to load configuration: {propsError}</p>
          )}

          {tab === 'actions' && !propsLoading && !propsError && rows.length > 0 && (
            <>
              {/* ── Action filter ── */}
              <div className={styles.filterRow}>
                <label className={styles.filterLabel} htmlFor="ccm-action-filter">
                  Filter by action
                </label>
                <select
                  id="ccm-action-filter"
                  className={styles.actionSelect}
                  value={selectedAction}
                  onChange={e => setUserAction(e.target.value)}
                >
                  <option value="__all__">All actions</option>
                  {actionOptions.map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>

              {/* ── Properties grid ── */}
              <div className={styles.gridWrapper}>
                <table className={styles.grid}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Action</th>
                      <th className={styles.th}>Property Key</th>
                      <th className={styles.th}>Value</th>
                      <th className={styles.th} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(row => {
                      const id = row._id
                      const isNew = newRowIds.has(id)
                      const isMultiline = isNew || row.value?.includes('\n') || row.value?.length > 80
                      // interval_seconds is the connector_actions column the orchestrator
                      // uses to schedule the action's next fire time, surfaced here as a
                      // reserved property key (not a real connector_props row) — every
                      // action always has one, so it can't be deleted like a custom prop.
                      const isInterval = row.prop_key === 'interval_seconds'
                      return (
                        <tr key={id} className={styles.tr}>
                          <td className={styles.td}>
                            {isNew ? (
                              <input
                                className={styles.cellInput}
                                value={row.action_name}
                                onChange={e => updateCell(id, 'action_name', e.target.value)}
                                placeholder="Action"
                              />
                            ) : (
                              <span className={styles.cellLabel}>{row.action_name}</span>
                            )}
                          </td>
                          <td className={styles.td}>
                            {isNew ? (
                              <input
                                className={styles.cellInput}
                                value={row.prop_key}
                                onChange={e => updateCell(id, 'prop_key', e.target.value)}
                                placeholder="Property key"
                              />
                            ) : (
                              <span className={styles.cellLabel}>{row.prop_key}</span>
                            )}
                          </td>
                          <td className={`${styles.td} ${styles.tdValue}`}>
                            {isInterval ? (
                              <input
                                className={styles.cellInput}
                                type="number"
                                min="1"
                                step="1"
                                value={row.value}
                                onChange={e => updateCell(id, 'value', e.target.value)}
                              />
                            ) : isMultiline ? (
                              <textarea
                                className={styles.cellTextarea}
                                value={row.value}
                                rows={4}
                                onChange={e => updateCell(id, 'value', e.target.value)}
                              />
                            ) : (
                              <input
                                className={styles.cellInput}
                                value={row.value}
                                onChange={e => updateCell(id, 'value', e.target.value)}
                              />
                            )}
                          </td>
                          <td className={styles.tdBtn}>
                            <button
                              className={`${styles.updateBtn} ${rowStatus[id] === 'error' ? styles.updateBtnError : ''}`}
                              onClick={() => handleUpdateRow(row)}
                              disabled={rowStatus[id] === 'saving' || rowStatus[id] === 'deleting'}
                            >
                              {rowStatus[id] === 'saving'
                                ? (isNew ? 'Saving…' : 'Updating…')
                                : rowStatus[id] === 'saved'
                                  ? (isNew ? 'Saved ✓' : 'Updated ✓')
                                  : rowStatus[id] === 'error'
                                    ? 'Failed — Retry'
                                    : (isNew ? 'Save' : 'Update')}
                            </button>
                            {/* A saved interval_seconds row can't be deleted (every action always
                                has one), but an unsaved new row can still be cancelled even if the
                                user happened to type that key into it. */}
                            {(!isInterval || isNew) && (
                              <button
                                className={styles.deleteBtn}
                                onClick={() => handleDeleteRow(row)}
                                disabled={rowStatus[id] === 'saving' || rowStatus[id] === 'deleting'}
                                aria-label="Delete property"
                                title="Delete property"
                              >
                                <Icon name={rowStatus[id] === 'deleting' ? 'spinner' : 'trash'} className={styles.deleteBtnIcon} spin={rowStatus[id] === 'deleting'} />
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {tab === 'actions' && (
            <Button variant="secondary" onClick={handleAddProperty} disabled={propsLoading}>New Property</Button>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>

      </div>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete property"
          message={`Delete "${pendingDelete.prop_key}" for action "${pendingDelete.action_name}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => performDelete(pendingDelete)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  )
}
